// ---------------------------------------------------------------------------
// Validations before simulating, replicating ComprobacionesPreEscritura()
// from the original (EvalHid_Git/EvalHid/EvalHid/Form1.vb, lines 1107-1795),
// which runs before EscribeModelo() (the routine that writes the input
// files for EvalHid_cal.exe). Each check is classified the same way as in
// the original, with one deliberate exception:
//   - BLOCKING: the original sets FaltanDatos=True and cuts with
//     Return/Exit Sub before writing anything — here that translates to
//     disabling "Calculate".
//   - NOTICE: the original shows an informational MsgBox but lets the user
//     continue (e.g. sub-basins without a zone, which simply get left out
//     of the calculation).
//
// Exception: gaps in data PER SUB-BASIN (missing own precipitation/ETP
// column, or missing temperature, or no outlet point assigned) are blocking
// in the original but are treated here as a notice — simulation is still
// allowed, excluding only the affected sub-basins, instead of blocking the
// whole project over a few incomplete sub-basins (decision saved in
// memory). Without an outlet point, the engine still calculates the
// sub-basin's contribution the same (see engine.ts, it doesn't check
// outletCode) — it just gets left out of the aggregation by outlet point
// (aggregation.ts already excludes it without extra work, since it groups
// by outletCode), but it's still visible in the "Sub-basin" tab of Results.
// ---------------------------------------------------------------------------
import type { DailySeries } from "./dailySeries.ts";
import { formatDateList, hasColumnAt } from "./dailySeries.ts";
import { findScaleDateGaps } from "./seriesScale.ts";
import type { Translate } from "../i18n/translate.ts";
import { NO_MODEL } from "./projectEntities.ts";
import type { EvalHidProject } from "./types.ts";

interface ProjectSeries {
  precipitation: DailySeries | null;
  etp: DailySeries | null;
  temperature: DailySeries | null;
  pumping: DailySeries | null;
}

export interface ValidationResult {
  /** Prevent simulating. */
  blocking: string[];
  /** Don't prevent simulating, just inform. */
  notices: string[];
}

/**
 * Validates a project before simulating, with the same rules as the
 * original (see the file header). Doesn't rewrite files or run any engine —
 * only decides whether "Calculate" can be enabled and what to warn about.
 */
export function validateProject(
  t: Translate,
  fullProject: EvalHidProject,
  series: ProjectSeries
): ValidationResult {
  const blocking: string[] = [];
  const notices: string[] = [];
  const project = fullProject;

  const withModel = project.subBasins.filter((c) => c.modelTypeCode !== NO_MODEL);
  const withSnow = project.subBasins.filter((c) => c.snowModelTypeCode !== NO_MODEL);

  // No zone (nor model): CompruebaModelosCuencas — notice only, they're left out of the calculation.
  const withoutModel = project.subBasins.length - withModel.length;
  if (withoutModel > 0) {
    notices.push(t("validations.noModelWarning", { n: withoutModel }));
  }

  // GR2M (modelTypeCode 4) is a genuinely MONTHLY formulation (fixed
  // ROUTING_CAPACITY_MM=60, see gr2m.ts's own header) — running it against
  // daily rows produces a real, silently wrong result, not just a style
  // mismatch. Notice, not blocking: the engine still runs (it's agnostic to
  // scale, see seriesScale.ts), the numbers are just wrong for this model.
  const gr2mSubBasins = withModel.filter((c) => c.modelTypeCode === 4);
  if (gr2mSubBasins.length > 0 && project.seriesScale !== "monthly") {
    notices.push(
      t("validations.gr2mNotMonthlyWarning", {
        n: gr2mSubBasins.length,
        list: gr2mSubBasins.map((c) => c.description).join(", "),
      })
    );
  }

  // No surface outlet: CompruebaPuntosDesague — blocking in the original for
  // ALL sub-basins; here it's a notice (see the exception in the file
  // header) — simulation is still possible, those sub-basins just get left
  // out of the aggregation by outlet point.
  const withoutOutlet = project.subBasins.filter((c) => c.outletCode == null);
  if (withoutOutlet.length > 0) {
    notices.push(
      t("validations.noOutletWarning", {
        n: withoutOutlet.length,
        list: withoutOutlet.map((c) => c.description).join(", "),
      })
    );
  }

  // Aquifers used by some sub-basin (COD_ACUIF_DESAGUE) without their own outlet point: CompruebaDatosAcuiferos — blocking.
  const usedAquifers = new Set(
    project.subBasins.filter((c) => c.aquiferCode !== 0).map((c) => c.aquiferCode)
  );
  const usedAquifersWithoutOutlet = project.aquifers.filter(
    (a) => usedAquifers.has(a.aquiferCode) && a.outletCode == null
  );
  if (usedAquifersWithoutOutlet.length > 0) {
    blocking.push(
      t("validations.aquifersWithoutOutletPointBlocking", {
        n: usedAquifersWithoutOutlet.length,
        list: usedAquifersWithoutOutlet.map((a) => a.name).join(", "),
      })
    );
  }

  // Repeated name between a sub-basin with a model and an aquifer in use: CompruebaCodigosCuencasAcuiferos — blocking
  // (the calculation engine identifies elements by name).
  const namesOfSubBasinsWithModel = new Set(withModel.map((c) => c.description));
  const collisions = project.aquifers
    .filter((a) => usedAquifers.has(a.aquiferCode))
    .map((a) => a.name)
    .filter((n) => namesOfSubBasinsWithModel.has(n));
  if (collisions.length > 0) {
    blocking.push(t("validations.duplicateName", { list: collisions.join(", ") }));
  }

  // No sub-basin with a model assigned: BT_Calcular_Click — blocking, there's nothing to simulate.
  if (project.subBasins.length > 0 && withModel.length === 0) {
    blocking.push(t("validations.noSubBasinWithModel"));
  }

  // Required series not loaded — blocking.
  if (!series.precipitation) blocking.push(t("validations.precipSeriesNotLoaded"));
  if (!series.etp) blocking.push(t("validations.etpSeriesNotLoaded"));

  // Calendar-day gaps within a loaded series' own date sequence (a day
  // missing entirely, not a cell with no value — that's the "invalid data"
  // notice further down). Neither the real engine (SeriesTemporales.cs) nor
  // this port's engine.ts checks the date sequence — both read row by row
  // assuming no gaps — so an undetected gap here would silently misalign
  // every row after it against the wrong calendar day. Blocking: unlike the
  // notices below, this can't be routed around by excluding a few
  // sub-basins — it means the file itself needs fixing. Decision saved in
  // memory (project_evalhid_series_gaps).
  for (const [key, s] of [
    ["precipDateGapsBlocking", series.precipitation],
    ["etpDateGapsBlocking", series.etp],
    ["temperatureDateGapsBlocking", series.temperature],
    ["pumpingDateGapsBlocking", series.pumping],
  ] as const) {
    if (!s) continue;
    const missing = findScaleDateGaps(s.dates, project.seriesScale);
    if (missing.length > 0) {
      blocking.push(t(`validations.${key}`, { n: missing.length, list: formatDateList(missing) }));
    }
  }

  // Sub-basins with a model but no own column in precipitation/ETP:
  // CompruebaPrecETP in the original was blocking for the whole project;
  // here calculation is still allowed and only those sub-basins are
  // excluded from the result (engine/engine.ts already skips them) —
  // decision saved in the project's memory: partial simulation instead of
  // all-or-nothing.
  if (series.precipitation && series.etp) {
    const withoutColumns = withModel.filter(
      (c) => !hasColumnAt(series.precipitation, c.precipCol) || !hasColumnAt(series.etp, c.etpCol)
    );
    if (withoutColumns.length > 0) {
      notices.push(
        t("validations.noColumnsWarning", {
          n: withoutColumns.length,
          list: withoutColumns.map((c) => c.description).join(", "),
        })
      );
    }
  }

  // Sub-basins with a snow zone but no temperature column: not excluded
  // from the calculation (unlike the previous case, if they have
  // precipitation/ETP they can still be simulated), but without
  // temperature the real snow model can't be applied to them — engine.ts
  // runs their normal rainfall-runoff model directly on the raw
  // precipitation instead, skipping only the Snow1/Snow2 step.
  if (withSnow.length > 0) {
    const withoutTemp = withSnow.filter((c) => !hasColumnAt(series.temperature, c.tempCol));
    if (withoutTemp.length > 0) {
      notices.push(
        t("validations.noTempWarning", {
          n: withoutTemp.length,
          list: withoutTemp.map((c) => c.description).join(", "),
        })
      );
    }
  }

  return { blocking, notices };
}
